import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { type Observable, switchMap } from 'rxjs';

import { environment } from '../../../../environments/environment';

import { type PagedReferenceValue, type ReferenceValue } from './reference-value.model';

@Injectable({
  providedIn: 'root'
})
export class ReferenceValueService {
  private http = inject(HttpClient);

  private readonly apiUrl = `${environment.apiBaseUrl}/referenceValue`;

  createReferenceValue(referenceValue: ReferenceValue): Observable<string> {
    const headers = new HttpHeaders().set('X-Spinner', 'true');
    return this.http.post(`${this.apiUrl}/create`, referenceValue, {
      headers,
      responseType: 'text'
    });
  }

  updateReferenceValue(referenceValue: ReferenceValue): Observable<string> {
    const headers = new HttpHeaders().set('X-Spinner', 'true');
    return this.http.put(`${this.apiUrl}/update`, referenceValue, {
      headers,
      responseType: 'text'
    });
  }

  getReferenceValueById(id: string): Observable<ReferenceValue> {
    const headers = new HttpHeaders().set('X-Spinner', 'true');
    return this.http.get<ReferenceValue>(`${this.apiUrl}/get/${id}`, { headers });
  }

  deleteReferenceValue(id: string): Observable<any> {
    const headers = new HttpHeaders().set('X-Spinner', 'true');
    return this.http.delete<any>(`${this.apiUrl}/hide/${id}`, { headers });
  }

  /**
   * For CRUD listing by referenceType (paged)
   */
  getPagedReferenceValuesByReferenceType(referenceTypeId: string, page: number, size: number, sort: string): Observable<PagedReferenceValue> {
    let params = new HttpParams().set('page', page.toString()).set('size', size.toString());
    if (sort) {
      params = params.set('sort', sort);
    }
    return this.http.get<PagedReferenceValue>(`${this.apiUrl}/find-all-referenceValues-by-referenceType/${referenceTypeId}`, { params });
  }

  /**
   * For dropdowns in other modules
   */
  getReferenceValuesForDropdownByType(referenceTypeInternalCode: string): Observable<ReferenceValue[]> {
    return this.http.get<ReferenceValue[]>(`${this.apiUrl}/find-all-referenceValues-for-dropdown-by-referenceType/${referenceTypeInternalCode}`);
  }

  getReferenceValueByTypeCodeAndLanguage$(typeCode: string, internalCode: string, langId$: Observable<string>): Observable<ReferenceValue> {
    return langId$.pipe(switchMap(langId => this.getReferenceValueByTypeCodeAndLanguage(typeCode, internalCode, langId)));
  }

  /**
   * Find a single ReferenceValue by referenceTypeInternalCode + internalCode + language
   */
  getReferenceValueByTypeCodeAndLanguage(referenceTypeInternalCode: string, internalCode: string, language: string): Observable<ReferenceValue> {
    const params = new HttpParams().set('internalCode', internalCode).set('language', language);
    return this.http.get<ReferenceValue>(`${this.apiUrl}/find-by-referenceType-and-internalCode-and-language/${referenceTypeInternalCode}`, { params });
  }

  /**
   * Utility for displaying labels in dropdowns etc.
   */
  getReferenceValueLabel(referenceValue: ReferenceValue): string {
    return referenceValue?.name ?? '';
  }

  getFileUploadLimitByRefValInternalCode(referenceValueInternalCode: string): Observable<ReferenceValue[]> {
    return this.http.get<ReferenceValue[]>(`${environment.apiBaseUrl}/kiClient/get-file-upload-limit/${referenceValueInternalCode}`);
  }
}
